/**
 * SystemNode - Fixed infrastructure node
 * Types: init, retrieval, reflection
 * These nodes are read-only and cannot be customized
 */

import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { SystemNodeData, SystemNodeType } from '../../../../stores/workflow/types';

interface SystemNodeProps {
  data: SystemNodeData;
  selected?: boolean;
}

const systemTypeConfig: Record<SystemNodeType, { color: string; bgGradient: string; icon: React.ReactNode; label: string }> = {
  init: {
    color: 'slate',
    bgGradient: 'from-slate-500 to-slate-700',
    label: 'Initialize',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
      </svg>
    )
  },
  retrieval: {
    color: 'teal',
    bgGradient: 'from-teal-500 to-teal-700',
    label: 'Retrieval',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4' />
      </svg>
    )
  },
  reflection: {
    color: 'indigo',
    bgGradient: 'from-indigo-500 to-indigo-700',
    label: 'Reflection',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' />
      </svg>
    )
  }
};

export const SystemNode = memo(({ data, selected }: SystemNodeProps) => {
  const config = systemTypeConfig[data.nodeType] || systemTypeConfig.init;

  return (
    <div
      className={`
        min-w-[180px] rounded-xl border-2 bg-white shadow-lg transition-all
        dark:bg-dark-background-secondary
        ${selected ? 'border-gray-400 ring-4 ring-gray-200/50' : 'border-gray-200 dark:border-border-dark'}
        cursor-not-allowed opacity-90
      `}
    >
      {/* Input Handle (top) */}
      <Handle type='target' position={Position.Top} className='!h-3 !w-3 !border-2 !border-white !bg-gray-400' />

      {/* Header with gradient */}
      <div className={`flex items-center gap-2 rounded-t-lg bg-gradient-to-r ${config.bgGradient} px-4 py-2`}>
        <div className='text-white'>{config.icon}</div>
        <div className='flex-1 font-semibold text-white'>{data.label}</div>
        {/* System badge */}
        <div className='rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-white'>System</div>
      </div>

      {/* Body */}
      <div className='px-4 py-3'>
        {/* Node Type Badge */}
        <div className='mb-2 flex items-center gap-2'>
          <span className='text-xs text-gray-500 dark:text-dark-text-secondary'>Type:</span>
          <span className='rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-dark-background-tertiary dark:text-dark-text-secondary'>
            {config.label}
          </span>
        </div>

        {/* Description */}
        {data.description && <div className='text-xs text-gray-500 dark:text-dark-text-secondary'>{data.description}</div>}

        {/* Fixed node indicator */}
        <div className='mt-2 rounded-lg bg-gray-100 p-2 text-xs text-gray-600 dark:bg-dark-background-tertiary dark:text-dark-text-secondary'>
          <div className='flex items-center gap-1'>
            <svg className='h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m0 0v2m0-2h2m-2 0H9m3-10V5a2 2 0 10-4 0v4H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2h-4V5a2 2 0 00-4 0' />
            </svg>
            <span>Fixed infrastructure</span>
          </div>
        </div>
      </div>

      {/* Output Handle (bottom) */}
      <Handle type='source' position={Position.Bottom} className={`!h-3 !w-3 !border-2 !border-white bg-gradient-to-r ${config.bgGradient}`} />
    </div>
  );
});

SystemNode.displayName = 'SystemNode';
